/**
 * Cross-vendor inventory — synthesized by normalizing the per-vendor fixture
 * data into the single InventoryDevice shape.
 *
 * Each entry maps a vendor's status string into our normalized DeviceStatus
 * enum so the chip filter works against one set of values.
 */
import type { InventoryDevice, DeviceStatus } from "@/api/schemas/devices";
import { MOCK_DEVICE_ROWS } from "@/api/fixtures/meraki-devices";
import { MOCK_ARUBA_DEVICES } from "@/api/fixtures/aruba-devices";

/** Map vendor-specific status strings into our normalized enum. */
function normalize(s: string | null | undefined): DeviceStatus {
  const lower = (s ?? "").toLowerCase();
  if (lower === "online" || lower === "up" || lower === "connected") return "online";
  if (lower.includes("alert") || lower.includes("reboot")) return "alerting";
  if (lower === "offline" || lower === "down") return "offline";
  return "unknown";
}

/** Cheap deterministic-ish health ratio based on status. */
function healthFromStatus(s: DeviceStatus): number {
  switch (s) {
    case "online":   return 0.99;
    case "alerting": return 0.72;
    case "offline":  return 0.05;
    default:          return 0.5;
  }
}

const fromMeraki: InventoryDevice[] = MOCK_DEVICE_ROWS.map((d) => {
  const status = normalize(d.status);
  return {
    vendor: "meraki" as const,
    serial: d.serial,
    name: d.name,
    model: d.model,
    mac: d.mac,
    status,
    site: "marriott-nyc-21-prod",
    groupOrNetwork: "L_111111111111111111",
    ipAddress: d.lanIp ?? null,
    firmware: d.firmware,
    tags: d.tags,
    healthRatio: healthFromStatus(status),
  };
});

const fromAruba: InventoryDevice[] = MOCK_ARUBA_DEVICES.map((d) => {
  const status = normalize(d.status);
  // ArubaDevice fields are now schema-nullable; InventoryDevice expects
  // string|undefined for optional ones. Coerce null → undefined.
  return {
    vendor: "aruba" as const,
    serial: d.serial,
    name: d.name,
    model: d.model ?? undefined,
    mac: d.macAddress ?? undefined,
    status,
    site: d.site,
    groupOrNetwork: d.group,
    ipAddress: d.ipAddress ?? null,
    firmware: d.firmware ?? undefined,
    tags: [],
    healthRatio: healthFromStatus(status),
  };
});

/**
 * Two synthesized Shasta entries — the legacy backend doesn't actually have
 * Shasta data yet but the dashboard counts a few. These keep the cross-vendor
 * filter chip useful.
 */
const fromShasta: InventoryDevice[] = [
  {
    vendor: "shasta",
    serial: "SH01-5K2N-1111",
    name: "shasta-gw-02",
    model: "Shasta GW · 2U",
    mac: "f8:33:a1:0c:55:11",
    status: "offline",
    site: "miami-jw-12-prod",
    groupOrNetwork: "miami-edge",
    ipAddress: null,
    firmware: "ShastaOS 4.2.1",
    tags: ["edge"],
    healthRatio: healthFromStatus("offline"),
  },
  {
    vendor: "shasta",
    serial: "SH01-5K2N-2222",
    name: "shasta-gw-03",
    model: "Shasta GW · 2U",
    mac: "f8:33:a1:0c:55:12",
    status: "online",
    site: "lab-slc-01-staging",
    groupOrNetwork: "lab-edge",
    ipAddress: "10.40.1.5",
    firmware: "ShastaOS 4.2.1",
    tags: ["lab"],
    healthRatio: healthFromStatus("online"),
  },
];

export const MOCK_INVENTORY_DEVICES: InventoryDevice[] = [
  ...fromMeraki,
  ...fromAruba,
  ...fromShasta,
];
